import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Close from '@/icons/Close';

import './member-filter-typeahead.css';

export interface MemberFilterOption {
  id: string;
  label: string;
}

interface MemberFilterTypeaheadProps {
  value: string | null;
  onChange: (memberId: string | null) => void;
  members: MemberFilterOption[];
  placeholder?: string;
  ariaLabel?: string;
  id?: string;
  showAllOption?: boolean;
  fullWidth?: boolean;
  /** Cap visible matches (default 5). Dropdown never scrolls; type to narrow further. */
  maxResults?: number;
}

const DEFAULT_MAX_RESULTS = 5;

const MemberFilterTypeahead: React.FC<MemberFilterTypeaheadProps> = ({
  value,
  onChange,
  members,
  placeholder,
  ariaLabel,
  id = 'member-filter-typeahead',
  showAllOption = true,
  fullWidth = false,
  maxResults = DEFAULT_MAX_RESULTS
}) => {
  const { t } = useTranslation('forms');
  const resolvedPlaceholder = placeholder ?? t('memberFilter.allMembers');
  const resolvedAriaLabel = ariaLabel ?? t('memberFilter.filterByMember');
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedMember = members.find((member) => member.id === value);

  const filteredMembers = (() => {
    const trimmed = query.trim().toLowerCase();
    if (!trimmed) return members;
    return members.filter((member) => member.label.toLowerCase().includes(trimmed));
  })();

  const totalFilteredCount = filteredMembers.length;
  const visibleMembers = filteredMembers.slice(0, maxResults);
  const hasMoreResults = totalFilteredCount > maxResults;

  const listItems: Array<{ type: 'all' } | { type: 'member'; member: MemberFilterOption }> = showAllOption
    ? [{ type: 'all' }, ...visibleMembers.map((member) => ({ type: 'member' as const, member }))]
    : visibleMembers.map((member) => ({ type: 'member' as const, member }));

  useEffect(() => {
    if (value && selectedMember) {
      setQuery(selectedMember.label);
    } else if (!value) {
      setQuery('');
    }
  }, [value, selectedMember]);

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current?.contains(event.target as Node) || inputRef.current?.contains(event.target as Node)) {
        return;
      }
      setOpen(false);
      setHighlightedIndex(-1);
      if (value && selectedMember) {
        setQuery(selectedMember.label);
      } else if (!value) {
        setQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, value, selectedMember]);

  const selectAll = () => {
    onChange(null);
    setQuery('');
    setOpen(false);
    setHighlightedIndex(-1);
  };

  const selectMember = (member: MemberFilterOption) => {
    onChange(member.id);
    setQuery(member.label);
    setOpen(false);
    setHighlightedIndex(-1);
  };

  const handleClear = () => {
    selectAll();
    inputRef.current?.focus();
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextQuery = event.target.value;
    setQuery(nextQuery);
    setOpen(true);
    setHighlightedIndex(-1);
    if (value && nextQuery !== selectedMember?.label) {
      onChange(null);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open && listItems.length > 0 && (event.key === 'ArrowDown' || event.key === 'Enter')) {
      setOpen(true);
      return;
    }

    if (!open) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setHighlightedIndex((prev) => (prev < listItems.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        event.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < listItems.length) {
          const item = listItems[highlightedIndex];
          if (item.type === 'all') selectAll();
          else selectMember(item.member);
        }
        break;
      case 'Escape':
        setOpen(false);
        setHighlightedIndex(-1);
        if (value && selectedMember) setQuery(selectedMember.label);
        else setQuery('');
        break;
      default:
        break;
    }
  };

  return (
    <div className={`member-filter-typeahead${fullWidth ? ' member-filter-typeahead--full-width' : ''}`}>
      <div className="member-filter-typeahead__wrapper">
        <input
          ref={inputRef}
          id={id}
          type="text"
          className="member-filter-typeahead__input"
          value={query}
          placeholder={resolvedPlaceholder}
          aria-label={resolvedAriaLabel}
          aria-expanded={open}
          aria-controls={`${id}-listbox`}
          aria-autocomplete="list"
          role="combobox"
          autoComplete="off"
          onChange={handleInputChange}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
        />
        {value && (
          <button
            type="button"
            className="member-filter-typeahead__clear"
            onClick={handleClear}
            aria-label={showAllOption ? t('memberFilter.clearFilter') : t('actions.clearSelection')}
          >
            <Close width={14} height={14} />
          </button>
        )}
      </div>

      {open && (
        <div
          ref={dropdownRef}
          id={`${id}-listbox`}
          className="member-filter-typeahead__menu member-filter-typeahead__menu--limited"
          role="listbox"
        >
          {listItems.length === 0 ? (
            <div className="member-filter-typeahead__empty">{t('memberFilter.noMatching')}</div>
          ) : (
            <>
              {listItems.map((item, index) => {
                if (item.type === 'all') {
                  return (
                    <button
                      key="all"
                      type="button"
                      role="option"
                      aria-selected={!value}
                      className={`member-filter-typeahead__option${
                        index === highlightedIndex ? ' member-filter-typeahead__option--highlighted' : ''
                      }${!value ? ' member-filter-typeahead__option--selected' : ''}`}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      onClick={selectAll}
                    >
                      {t('memberFilter.allMembers')}
                    </button>
                  );
                }

                const isSelected = value === item.member.id;
                return (
                  <button
                    key={item.member.id}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    className={`member-filter-typeahead__option${
                      index === highlightedIndex ? ' member-filter-typeahead__option--highlighted' : ''
                    }${isSelected ? ' member-filter-typeahead__option--selected' : ''}`}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    onClick={() => selectMember(item.member)}
                  >
                    {item.member.label}
                  </button>
                );
              })}
              {hasMoreResults && (
                <div className="member-filter-typeahead__footer">
                  {query.trim()
                    ? t('memberFilter.showingTop', { shown: maxResults, total: totalFilteredCount })
                    : t('memberFilter.membersTypeToFilter', { count: members.length })}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default MemberFilterTypeahead;
